import { StrategyBuilderFunction, EMPTY_STRATEGY } from './types';
import { addLongPut, addShortPut } from './addOption';
import {
  getDefaultExpiration,
  findClosestStrikePrice,
  getStrikesAtExpiration,
  expirationToDate,
  getPremium,
  getStrategyLegStrikeDirection,
  getStrikeDistance,
} from './utils';

export const putBrokenWing: StrategyBuilderFunction = ({
  chain,
  spot,
  expirationDate,
  expirationKey,
  strikeDirection,
  contractMultiplier = 1,
}) => {
  const strikesAtExpiration = expirationKey
    ? getStrikesAtExpiration(chain, expirationKey)
    : getDefaultExpiration(chain);

  if (
    !strikesAtExpiration ||
    !strikesAtExpiration.e ||
    !strikesAtExpiration.ey ||
    !strikesAtExpiration.em ||
    !strikesAtExpiration.ed
  ) {
    return EMPTY_STRATEGY;
  }

  const expirationDateForOptions =
    expirationDate ||
    expirationToDate(strikesAtExpiration.ey, strikesAtExpiration.em, strikesAtExpiration.ed);
  const expirationKeyForOptions = expirationKey || strikesAtExpiration.e;

  // Choosing strike prices
  const itmPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: -2 }),
  ); // Buying 1 ITM put
  const atmPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 1 }),
  ); // Selling 2 ATM puts
  // The OTM put strike needs to be chosen such that the spread is wider on the downside
  const otmPutStrike = findClosestStrikePrice(
    strikesAtExpiration,
    getStrikeDistance({ price: spot, unit: 2 }),
  ); // Buying 1 OTM put

  return {
    optionPositions: [
      // Buy 1 ITM put option
      addLongPut({
        strike: itmPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[itmPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[itmPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
      // Sell 2 ATM put options
      addShortPut({
        strike: atmPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[atmPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[atmPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'short'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 2 * contractMultiplier,
      }),
      // Buy 1 OTM put option, further OTM than the traditional butterfly spread
      addLongPut({
        strike: otmPutStrike,
        premium: getPremium({
          ask: strikesAtExpiration?.c?.[otmPutStrike]?.p?.a || 0,
          bid: strikesAtExpiration?.c?.[otmPutStrike]?.p?.b || 0,
          strikeDirection: getStrategyLegStrikeDirection(strikeDirection, 'long'),
        }),
        expirationDate: expirationDateForOptions,
        expirationKey: expirationKeyForOptions,
        numberOfContracts: 1 * contractMultiplier,
      }),
    ],
    stockPositions: [],
  };
};
